/**
 * §1 Hero —— cjwf-dark-banner
 *
 * 需求来源：docs/万里汇合作落地页/万里汇合作落地页-提示词.md 第1次提交（任务单 DEV-124229）；
 *           原型 L1909-2398。
 *
 * 注意事项：
 * 1. 【SEO】原型全文 0 个 <h1>（全是 h2/h3），落地页缺 h1 不利于收录。按 D7⑥ 把本区块主标题
 *    渲染为 <h1>，**类名仍用 .cjwf-dark-title**，故视觉零差异；单测 CASE-H1 断言全页恰好 1 个 h1。
 * 2. 【文案】原型 L1935 在 </span> 与 'Got You Covered' 之间烤进了 8 个字面空格，未复刻——
 *    间距交给 JSX 结构与 CSS（见 constants.js 对应注释）。
 * 3. 背景大图与光晕均由 CSS 负责（.cjwf-dark-banner::before 等），此处只出结构。
 * 4. 本区块 13 个动画全部是加载即启动的 CSS 循环，无滚动触发逻辑，故组件内无任何副作用。
 */
import React from 'react';
import EmphasisText from './EmphasisText';
import { IconCheck, IconArrowRightLg, IconLock, IconArrowRightSm } from './Icons';

export default function Hero({ data }) {
  if (!data) return null;
  const d = data.dashboard || {};

  return (
    <section className="cjwf-dark-banner">
      <div className="cjwf-dark-pattern" />
      <span className="cjwf-dark-glow cjwf-dark-glow--pink" />
      <span className="cjwf-dark-glow cjwf-dark-glow--orange" />

      <div className="cjwf-dark-container">
        <div className="cjwf-dark-content">
          {/* 【D7⑥】原型此处是 h2，改为 h1（类名不变，视觉一致） */}
          <h1 className="cjwf-dark-title">
            {data.titleLine1}
            <span className="cjwf-dark-title-highlight">{data.titleEm}</span>
            {data.titleLine2}
          </h1>

          <p className="cjwf-dark-description">{data.desc}</p>

          <div className="cjwf-dark-benefits">
            {(data.benefits || []).map((b, i) => (
              <div className="cjwf-dark-benefit" key={i}>
                <span className="cjwf-dark-benefit-icon">
                  <IconCheck />
                </span>
                {/* 【文字必须包在自己的 <span> 里】.cjwf-dark-benefit 是 `display:flex; gap:12px`。
                    原型把整段文字（含其中的 <strong>）装在一个 span 里 —— 那是**一个** flex item，
                    内部正常按文字流换行。若让文本节点与 <strong> 直接成为 .cjwf-dark-benefit 的子节点，
                    它们会各自变成**独立 flex item**，换行点完全不同：375px 下第 3 条高 81px、原型只有 62px。
                    所以这一层 span 是必需的，强调词内嵌到词条后也要保留它当宿主。 */}
                <span>
                  <EmphasisText text={b.text} />
                </span>
              </div>
            ))}
          </div>

          <div className="cjwf-dark-actions">
            <a
              className="cjwf-dark-button"
              href={data.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.ctaText}
              <span className="cjwf-dark-button-icon">
                <IconArrowRightLg />
              </span>
            </a>
            <span className="cjwf-dark-note">{data.note}</span>
          </div>
        </div>

        <div className="cjwf-dark-visual">
          <div className="cjwf-dark-visual-frame">
            <div className="cjwf-dark-floating cjwf-dark-floating--top">
              <strong>{data.floatTop.value}</strong>
              <span>{data.floatTop.label}</span>
            </div>

            <div className="cjwf-dark-dashboard">
              <div className="cjwf-dark-dashboard-header">
                <div>
                  <div className="cjwf-dark-dashboard-label">{d.label}</div>
                  <div className="cjwf-dark-dashboard-title">{d.title}</div>
                </div>
                <div className="cjwf-dark-secure">
                  <IconLock />
                  {d.secure}
                </div>
              </div>

              <div className="cjwf-dark-account">
                <div className="cjwf-dark-account-top">
                  <div className="cjwf-dark-account-label">{d.accountLabel}</div>
                  <div className="cjwf-dark-account-status">
                    <span className="cjwf-dark-status-dot" />
                    {d.accountStatus}
                  </div>
                </div>
                <div className="cjwf-dark-balance">{d.balance}</div>
                <div className="cjwf-dark-account-footer">
                  <span>{d.accountFoot1}</span>
                  <span>{d.accountFoot2}</span>
                </div>
              </div>

              <div className="cjwf-dark-currencies">
                {(d.currencies || []).map((c, i) => (
                  <div className="cjwf-dark-currency" key={i}>
                    <div className="cjwf-dark-currency-code">{c.code}</div>
                    <div className="cjwf-dark-currency-value">{c.value}</div>
                  </div>
                ))}
              </div>

              <div className="cjwf-dark-transaction">
                <div className="cjwf-dark-transaction-left">
                  <span className="cjwf-dark-transaction-icon">
                    <IconArrowRightSm />
                  </span>
                  <div>
                    <div className="cjwf-dark-transaction-label">{d.txnLabel}</div>
                    <div className="cjwf-dark-transaction-meta">{d.txnMeta}</div>
                  </div>
                </div>
                <div className="cjwf-dark-transaction-amount">
                  <div className="cjwf-dark-transaction-value">{d.txnValue}</div>
                  <div className="cjwf-dark-transaction-status">{d.txnStatus}</div>
                </div>
              </div>

              <div className="cjwf-dark-dashboard-footer">
                {/* 两个小圆点是**markup 元素**不是伪元素（.cjwf-dark-dashboard-feature 为
                    `display:flex; gap:6px`，其 ::before 无规则）：橙点配 Global collections、
                    粉点配 Supplier payments，样式取自 .cjwf-dark-feature-dot--orange/--pink
                    （7×7 圆点 + currentColor 光晕）。我最初漏了这两个 span。 */}
                <div className="cjwf-dark-dashboard-feature">
                  <span className="cjwf-dark-feature-dot cjwf-dark-feature-dot--orange" />
                  {d.feature1}
                </div>
                <div className="cjwf-dark-dashboard-feature">
                  <span className="cjwf-dark-feature-dot cjwf-dark-feature-dot--pink" />
                  {d.feature2}
                </div>
              </div>
            </div>

            <div className="cjwf-dark-floating cjwf-dark-floating--bottom">
              <strong>{data.floatBottom.value}</strong>
              <span>{data.floatBottom.label}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
